import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  EmailField,
  Form,
  PasswordField,
  Submit,
  TextField,
} from '@src/corelib/forms/generic';
import { createSchema, Yup } from '@src/corelib/validation';
import {
  usePasswordResetConfirm,
  usePasswordResetRequest,
} from '@src/query';
import { Link, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import AuthLayout from '@src/pages/Auth/AuthLayout.jsx';

const StyleSheet = {
  links: {
    display: 'flex',
    justifyContent: 'center',
    '& a': (theme: Theme) => ({
      color: theme.palette.common.black,
      textDecoration: 'none',
    }),
  },
};

type PasswordRequestFormValues = {
  email: string;
};

type PasswordConfirmFormValues = {
  password: string;
  otp_code: string;
};

const requestSchema = createSchema({
  email: { type: 'email', label: 'Email' },
});

const confirmSchema = createSchema({
  password: { type: 'password', label: 'New password' },
  otp_code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Enter a valid 6-digit code'),
});

const REQUEST_DEFAULTS = { email: '' };
const CONFIRM_DEFAULTS = { password: '', otp_code: '' };
type RequestValues = typeof REQUEST_DEFAULTS;
type ConfirmValues = typeof CONFIRM_DEFAULTS;
type PasswordResetSearch = { token?: string };

function PasswordReset() {
  const search = useSearch({ strict: false }) as PasswordResetSearch;
  const token = search.token ?? '';
  const requestMutation = usePasswordResetRequest();
  const confirmMutation = usePasswordResetConfirm();

  const mode = useMemo(() => (token ? 'confirm' : 'request'), [token]);

  const submitRequest = (values: RequestValues) =>
    requestMutation.mutateAsync(values);
  const submitConfirm = (values: ConfirmValues) =>
    confirmMutation.mutateAsync({ ...values, token });

  return (
    <AuthLayout>
      <Typography variant="h4" align="center">
        Password Reset
      </Typography>

      {mode === 'request' && !requestMutation.isSuccess && (
        <>
          <Typography variant="body2" align="center">
            Enter your email. Reset works only if 2FA is enabled for your
            account.
          </Typography>
          <Form
            schema={requestSchema}
            defaultValues={REQUEST_DEFAULTS}
            sx={{ width: '100%' }}
            onSubmit={(values) => submitRequest(values as PasswordRequestFormValues)}
          >
            <EmailField required fullWidth name="email" label="Email" />
            <Submit fullWidth variant="contained">
              Send reset link
            </Submit>
          </Form>
        </>
      )}

      {mode === 'request' && requestMutation.isSuccess && (
        <Typography variant="body2" align="center">
          If your account is eligible, a reset link was sent to your email.
        </Typography>
      )}

      {mode === 'confirm' && !confirmMutation.isSuccess && (
        <>
          <Typography variant="body2" align="center">
            Set a new password and enter your authenticator code.
          </Typography>
          <Form
            schema={confirmSchema}
            defaultValues={CONFIRM_DEFAULTS}
            sx={{ width: '100%' }}
            onSubmit={(values) => submitConfirm(values as PasswordConfirmFormValues)}
          >
            <PasswordField
              required
              fullWidth
              name="password"
              label="New password"
            />
            <TextField
              required
              fullWidth
              name="otp_code"
              label="Authenticator code"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
            <Submit fullWidth variant="contained">
              Set new password
            </Submit>
          </Form>
        </>
      )}

      {mode === 'confirm' && confirmMutation.isSuccess && (
        <Typography variant="body2" align="center">
          Password changed successfully.
        </Typography>
      )}

      <Box sx={StyleSheet.links}>
        <Link to="/auth/login">Back to login</Link>
      </Box>
    </AuthLayout>
  );
}

PasswordReset.displayName = 'PasswordReset';
export default PasswordReset;
